import React, { useContext, useMemo } from 'react';
import {
  Button, Label, Popup, Progress, Icon,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import styled, { ThemeContext } from 'styled-components';
import util from 'utils/utils';
import moment from 'moment';

import EmojiChooser from 'components/lib/Emoji/EmojiChooser';
import Emoji from 'components/lib/Emoji/Emoji';
import { ShadowedCard, FakeLink } from 'components/lib/UI';
import { IdeaAssessmentReport } from 'components/lib/Ideas';
import PinIdeaHelper from 'components/lib/Ideas/PinIdeaHelper';

const IdeaCardComponent = styled(ShadowedCard)`
  .cover-header{
    width: 100%;
    height: 75px;
    background-size: cover;
    overflow: hidden;
    background-repeat: no-repeat;
    background-position: center center;
    ${(props) => !props.hasProgress && `
      border-top-left-radius: 7px;
      border-top-right-radius: 7px;
    `}
  }
  .name-container{
    width: 100%;
    overflow-wrap: wrap;
  }
  .name-text{
    max-width: 100%;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .submitted-label{
    color: #a3ded8;
    float: 'right';
  }
  .status-label {
    position: absolute;
    top: 5px;
    right: 5px;
    margin-top: 35px;
  }
  .draft-label{
    color: #f9dab3;
    padding: 20;
    float: 'right';
  }
  .footer-container{
    text-align: center;
    font-weight: 600;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 !important;
    border-bottom-left-radius: 7px;
    border-bottom-right-radius: 7px;
  }
  .owner-challenge-link{
    margin-bottom: 5px
  }
  .owner-name {
    border-bottom: 1px dotted ${({ theme }) => theme.primaryColour};
  }
`;

const SubmissionLabel = styled(Label)`
  &&&& {
    margin-top: 7px;
    background-color: ${(props) => props.bgcolor || '#a3ded8'};
    color: ${({ textcolor }) => textcolor ?? 'inherit'};
    ${(props) => props?.labelposition === 'right' && `
    position: absolute;
    right: -4px;
  `}
  }
`;

const IdeaCard = ({
  onHeaderClicked, user, idea, challenge, t, footer, themedFooter, isYours, pinIdea, projectManagement, labelIdea, onSelect, isSelected, removeAssessors, invitations, fetchIdeas,
}) => {
  const theme = useContext(ThemeContext);
  const pmBoard = projectManagement?.boards?.filter((b) => b.forId === challenge?._id)[0];
  const pmBoardEnabled = pmBoard?.isEnabled;

  const completenessPercentage = (idea.completeness || 0) * 100;
  const ideaComplete = idea.completeness && (idea.completeness === 1);

  const hasAssessments = useMemo(() => idea.assessments?.length > 0 || idea?.invitedExternalAssessors?.length > 0, [idea]);
  const submittedAssessments = idea.assessments?.filter((a) => a.isSubmitted).length;
  const pendingInvitations = invitations && invitations.filter((i) => i?.forIdeas && i.forIdeas.includes(idea._id));

  const {
    activityInfo = {},
    createdAt = 0,
    updatedAt,
  } = idea;

  const { lastActivityAt = updatedAt ?? createdAt } = activityInfo;

  const momentLastActivityAt = moment(lastActivityAt);
  const momentCreatedAt = moment(createdAt);
  const threeDaysAgo = moment().subtract(3, 'days').valueOf();
  const oneDayAgo = moment().subtract(1, 'day').valueOf();
  const isNewIdea = (momentCreatedAt.valueOf() > threeDaysAgo);
  const isHotIdea = (momentLastActivityAt.valueOf() > oneDayAgo);

  return (
    <IdeaCardComponent
      style={isYours ? { borderBottom: `5px solid ${theme.primaryColour}` } : null}
      hasProgress={completenessPercentage > 0 && !idea.isSubmitted}
    >
      {(completenessPercentage && !idea.isSubmitted)
        ? (
          <Popup
            content={`Idea is ${completenessPercentage}% complete`}
            trigger={
              <Progress size='large' style={{ height: '0.6rem' }} label={`${completenessPercentage}% complete`} indicating={!ideaComplete} success={ideaComplete} percent={completenessPercentage} attached='top' />
            }
          />
        ) : null
      }
      <Link to={`/ideas/${idea._id}`} onClick={onHeaderClicked && ((e) => { onHeaderClicked(); e.preventDefault(); })}>
        <div style={{ position: 'relative', backgroundImage: `url(${util.ideaCoverImage(idea)})` }} className='cover-header'>
          <div style={{
            position: 'absolute', width: '100%', height: '100%', background: 'rgba(0,0,0,0.2)',
          }} />
          <div>
            {(isNewIdea || isHotIdea)
              ? (
                <SubmissionLabel
                  size='small'
                  bgcolor={isNewIdea ? '#18AE9f' : '#BF2713'}
                  textcolor='#fff'
                >
                  {isNewIdea ? 'NEW!' : 'HOT!'}
                </SubmissionLabel>
              ) : null}
            {idea.isSubmitted
              ? <SubmissionLabel size='small' bgcolor='#a3ded8' textcolor='#18ae9f' labelposition='right' >{t('ideas.card.submitted')}</SubmissionLabel>
              : <SubmissionLabel size='small' bgcolor='#f9dab3' labelposition='right'>{t('ideas.card.draft')}</SubmissionLabel>
            }
          </div>
          {util.canManageChallenge(user, challenge)
            ? <div className='status-label'>
              {labelIdea
                && <Popup on='hover' hoverable position='bottom center' basic
                  mouseEnterDelay={300}
                  trigger={<Button circular size='mini' icon='smile outline' onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} />}
                  content={<div style={{ width: 400 }}>
                    <h4>Label this idea</h4>
                    <EmojiChooser onComplete={(em) => labelIdea(idea._id, em, true)} existing={idea.stamps} hideExisting onRemoveExisting={(em) => labelIdea(idea._id, em, false)} />
                  </div>}
                />
              }
              {pinIdea
                ? (
                  <Popup
                    mouseEnterDelay={100}
                    trigger={(
                      <Button size='mini' inverted={!idea.isPinned} basic={!idea.isPinned} color='teal' icon='pin' onClick={(e) => { pinIdea(idea._id, !idea.isPinned); e.preventDefault(); e.stopPropagation(); }} />
                    )}
                    content={(
                      <div>
                        <p><b>{idea.isPinned ? t('ideas.options.pin.unPin') : t('ideas.options.pin.challenge')}</b></p>

                        <PinIdeaHelper challenge={challenge} />
                      </div>
                    )}
                  />
                ) : null
              }
              {onSelect && <Button size='mini' icon={isSelected ? 'check' : 'check'} primary={isSelected} basic={!isSelected} inverted={!isSelected} onClick={(e) => { onSelect(idea); e.preventDefault(); e.stopPropagation(); }} />}
            </div>
            : <div className='status-label'>
              {idea.isPinned && <Label size='tiny' color='teal' icon='pin' />}
            </div>
          }
        </div>
      </Link>

      <div className='content'>
        <div className='name-container'>

          <h3 className='name-text'>
            <Link to={`/ideas/${idea._id}`} style={{ color: '#4d4949' }}>{idea.children ? <Icon name='sitemap' size='small' /> : null}{idea.name}</Link>
          </h3>
        </div>
        {idea.ownerChallenge
          && <div className='owner-challenge-link'>
            <Link style={{ textDecorationLine: 'underline' }} to={`/challenges/${idea.ownerChallenge._id}`} className='meta'>{idea.ownerChallenge?.name}</Link>
          </div>

        }

        {idea?.owner?._id ? (
          <div className='owner-link'>
            {idea.businessName
              ? (
                <FakeLink>{idea.businessName}</FakeLink>
              ) : (
                <Link className='owner-name' to={`/users/${idea?.owner?._id}`}>{idea.owner.profile.fullName}</Link>
              )
            }
            {((idea.collaborators?.length || !!idea.businessName) && idea.authors?.length)
              ? (
                <Popup
                  on='click'
                  disabled={(!idea.businessName && ((idea.authors?.length || 0) < 2))}
                  content={(idea.authors?.length && idea.authors.filter((a) => !!idea.businessName || (a._id !== idea.user)).length)
                    ? (
                      idea.authors.filter((a) => !!idea.businessName || (a._id !== idea.user)).map((author) => (
                        <Link to={`/users/${author._id}`}>{author.profile.fullName}</Link>
                      )).reduce((prev, curr) => [prev, ', ', curr])
                    ) : null
                  }
                  trigger={(
                    <FakeLink>{` + ${util.pluralise((idea.businessName ? ((idea.collaborators?.length ?? 0) + 1) : (idea.collaborators?.length ?? 0)), 'other', 'others')}`}</FakeLink>
                  )}
                />
              ) : null
            }
            <p style={{ marginTop: 4, fontSize: 13, color: 'grey' }}>{idea.isSubmitted ? `${t('ideas.card.submittedOn', { date: moment(idea.submittedAt).format('DD/MM/YY') })}` : `${t('ideas.card.updatedAt', { date: moment(idea.updatedAt).format('DD/MM/YY') })}`}
              , last activity on {momentLastActivityAt.format('DD/MM/YY')}.
            </p>
          </div>) : null}

        {util.canManageChallenge(user, challenge) && pmBoardEnabled && pmBoard?.laneObject
          && <div style={{ marginTop: 5, display: 'inline-flex' }}><Icon style={{ color: '#aab6b2' }} name='trello' /><p style={{ color: '#aab6b2' }}>{pmBoard.laneObject?.name}</p></div>
        }

        <div style={{
          display: 'flex', maxWidth: '100%', flexWrap: 'wrap', marginTop: 5,
        }}>
          {(hasAssessments)
            && <Popup
              trigger={pendingInvitations?.length > 0
                ? <Label style={{ cursor: 'pointer' }} color='violet' size='tiny' >{Math.round(idea.assessmentSubmittedScore || 0)}% from {submittedAssessments || 0}/{idea.assessments?.length || 0} assessors ({pendingInvitations?.length} pending)</Label>
                : <Label style={{ cursor: 'pointer' }} color='violet' size='tiny' >{Math.round(idea.assessmentSubmittedScore || 0)}% from {submittedAssessments || 0}/{idea.assessments?.length || 0} assessors</Label>
              }
              style={{ zIndex: 100 }}
              on='click' basic
              content={(
                <div style={{ width: 500 }}>
                  <IdeaAssessmentReport fetchIdeas={fetchIdeas} challenge={challenge} idea={idea} removeAssessors={removeAssessors} />
                </div>
              )}
            />
          }
          {idea.stamps && idea.stamps.length ? idea.stamps.map((stamp, index) => <div key={index} className='emoji'>
            {util.canManageChallenge(user, challenge)
              ? <Emoji emoji={{ id: stamp }} size={20} onClick={(em) => labelIdea(idea, em.id, false)} />
              : <Emoji emoji={{ id: stamp }} size={20} />
            }
          </div>) : null}
        </div>
      </div>
      <div className='extra-content footer-container' style={themedFooter ? { backgroundColor: theme.primaryColour } : null}>
        {footer}
      </div>
    </IdeaCardComponent>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

const IdeaCardContainer = connect(
  mapStateToProps,
)(IdeaCard);

export default withTranslation()(IdeaCardContainer);
